<template>
  <div v-style:bg="backgroundColor" :class="{ 'py-6': backgroundColor }">
    <div :class="insetClasses">
      <div
        v-if="title || subtitle || textLink"
        class="mb-4 f-col md:flex-row md:items-end"
        :class="[
          alignment === 'center' ? 'items-center text-center md:justify-center' : 'md:between',
          titlePadding,
        ]"
      >
        <div class="space-y-2 lg:space-y-4">
          <component
            :is="titleTag"
            v-if="title"
            v-style:c="titleColor"
            :class="titleStyle"
          >
            <span v-if="$feature.showTitleHtml" v-html="titleHtml" />
            <span v-else>{{ title }}</span>
          </component>
          <p v-if="subtitle" v-style:c="subtitleColor" :class="subtitleStyle">
            <span v-if="$feature.showTitleHtml" v-html="subtitleHtml" />
            <span v-else>{{ subtitle }}</span>
          </p>
        </div>
        <cms-shared-button
          v-if="textLink"
          v-bind="{ ...textLink, ...getTextLinkStyles(content) }"
        />
      </div>
      <div
        v-style="{ gap }"
        class="grid md:cols-2"
        :class="!carouselOnMobile && '<md:auto-rows-fr'"
      >
        <div class="h-full" :class="isPrimaryRight && 'md:order-last'">
          <cms-linked-card :content="primaryCard" full-height />
        </div>
        <div
          class="overflow-hidden"
          :class="{
            'relative': !carouselOnMobile,
            'md:relative': carouselOnMobile,
            'md:order-first': isPrimaryRight,
            '<md:-mx-4': carouselOnMobile && componentSidePaddings?.sm,
          }"
        >
          <vf-carousel-elevate
            v-if="carouselOnMobile"
            align="start"
            class="md:hidden o:overflow-visible"
            class-container="o:gap-$gap o:md:gap-$gap-md o:lg:gap-$gap-lg"
            loop
            :show-arrows="false"
            show-pagination
          >
            <div
              v-for="card in secondaryCards"
              :key="card.id"
              v-style="{ gap }"
              class="self-stretch"
              :class="{ 'first:pl-4': componentSidePaddings?.sm }"
              :style="{
                width: slideWidth,
              }"
            >
              <cms-linked-card :content="card" full-height />
            </div>

            <template #pagination="{ activeItem, total, scrollTo }">
              <vf-carousel-pagination-dots
                :active="activeItem"
                class="mt-4 justify-center"
                :total
                :variant="backgroundType"
                @update:active="scrollTo($event)"
              />
            </template>
          </vf-carousel-elevate>
          <div
            v-style="{ gap }"
            class="absolute-0 overflow-auto"
            :class="[
              secondaryCards.length === 4 ? 'grid cols-2 auto-rows-fr' : 'f-col',
              carouselOnMobile && '<md:hidden',
            ]"
          >
            <cms-linked-card
              v-for="card in secondaryCards"
              :key="card.id"
              :class="secondaryCards.length !== 4 && 'flex-1 min-h-0'"
              :content="card"
              full-height
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Responsive } from '#types/common'
import type { CollectionLinkedCardColumn } from '#types/components/cms/collection'

const { content, firstElement, pageContext } = defineProps<{
  content: CollectionLinkedCardColumn
  firstElement?: boolean
  pageContext?: boolean
}>()

const {
  alignment,
  carouselOnMobile,
  componentSidePaddings,
  backgroundColor,
  backgroundType,
  items,
  primaryCardPosition,
  subtitle,
  subtitleColor,
  subtitleHtml,
  subtitleStyle,
  textLink,
  title,
  titleColor,
  titleHtml,
  titleStyle,
} = content

const { breakpoints } = useAppConfig().ds
const { cms } = useAppConfig().components
const { spacing } = cms.collection
const insetClasses = getContentInset(content, cms.section)
const titlePadding = pageContext ? getInvertedPaddings(content, cms.section) : ''

const titleTag = firstElement ? 'h1' : 'h2'

const [primaryCard, ...secondaryCards] = items

const isPrimaryRight = primaryCardPosition === 'right'

const gap = Object.keys(breakpoints)
  .reduce((acc, bp) => ({
    ...acc,
    ...(content.breakpoints[bp]?.slidePadding && {
      [bp]: `${spacing[content.breakpoints[bp].slidePadding] * 2 || 0}rem`
    })
  }), {} as Responsive)

const slideWidth = computed(() => {
  const key = content.breakpoints.sm?.slidePadding
  const padding = 0.5 + (key ? (spacing[key] || 0) / 2 : 0)
  const percentage = secondaryCards.length > 3 ? '50%' : '100%'

  return `calc(${percentage} - ${padding}rem)`
})
</script>
